import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { catalog } from '@atlassiansox/metal-client';
import debounce from 'lodash.debounce';
import React, { useState } from 'react';
import { metalClient } from '../../../../metrics';
import { useSimpleSignupState } from '../../hook';
import { messages } from '../../messages';
import {
  updateN2NDeferredPasswordFieldProgressEmail,
  updateN2NDeferredPasswordFieldValidationEmail,
  useAppDispatch,
  useAppSelector
} from '../../../../redux';
import { AuthenticationMethod, AuthenticationMethodsType } from '../../types';
import { FieldType } from '../model';
import { ErrorMessage, FieldWrapper } from '../styled';
import { IconValidation } from './icon-validation';

export function InputEmail() {
  const { intl, signInAccountUrl } = useSimpleSignupState();
  const dispatch = useAppDispatch();
  const validationState = useAppSelector(
    ({ n2nFieldValidation: { email } }) => email
  );
  const stargateClient = useStargateClient();
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [hasFocus, setHasFocus] = useState(false);
  const [validationMessage, setValidationMessage] = useState<any>();
  const emailMatch =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const emailExistsAnalytics = () => {
    createAnalyticsEvent({
      eventName: 'wac-signup-existing-user-signin',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
  };

  async function checkEmailSso(email: string) {
    const emailCheck = await stargateClient.checkAuthenticationMethodsAvailable(
      { email, successRedirect: window.location.href, webPlatform: 'magnolia' }
    );
    const isSaml = emailCheck.methods.some((method: AuthenticationMethod) => {
      return (
        method.type === AuthenticationMethodsType.SAML ||
        method.type === AuthenticationMethodsType.GOOGLE
      );
    });
    const containsPassword = emailCheck.methods.some(
      (method: AuthenticationMethod) => {
        return method.type === AuthenticationMethodsType.PASSWORD;
      }
    );

    // new or existing user sign in via redirect to sso or google
    if (isSaml && !containsPassword) {
      const redirectUrls = emailCheck.methods
        .filter((method: AuthenticationMethod) => {
          if (
            (method.type === AuthenticationMethodsType.SAML ||
              method.type === AuthenticationMethodsType.GOOGLE) &&
            method.redirect_to !== undefined
          ) {
            return method.redirect_to;
          }
          return;
        })
        .map((i) => i.redirect_to);
      dispatch(updateN2NDeferredPasswordFieldValidationEmail('error'));
      metalClient.error.submit({
        name: catalog.error.INPUT,
        page: 'sign-up-layout-simple-signup',
        component: 'simple-signup-input-email'
      });
      setValidationMessage(
        <div onClick={emailExistsAnalytics}>
          <SanitizedFormattedHtmlMessage
            {...messages.errorSsoInUse}
            values={{
              signInAccountUrl: redirectUrls[0]
            }}
          />
        </div>
      );
      // existing user, sign in via redirect to identity
      // @ts-ignore (removal requires updated asc-stargate type)
    } else if (containsPassword && emailCheck.userExists === true) {
      dispatch(updateN2NDeferredPasswordFieldValidationEmail('error'));
      metalClient.error.submit({
        name: catalog.error.INPUT,
        page: 'sign-up-layout-simple-signup',
        component: 'simple-signup-input-email'
      });
      setValidationMessage(
        <div onClick={emailExistsAnalytics}>
          <SanitizedFormattedHtmlMessage
            {...messages.errorEmailInUse}
            values={{
              signInAccountUrl
            }}
          />
        </div>
      );
      // user doesnt exist, sign up normally
    } else {
      dispatch(updateN2NDeferredPasswordFieldValidationEmail('success'));
      dispatch(updateN2NDeferredPasswordFieldProgressEmail(true));
      setValidationMessage(undefined);
    }
    return undefined;
  }

  const checkEmailAsyncDebounced = debounce(
    async (value: string) => {
      return checkEmailSso(value);
    },
    750,
    { trailing: true }
  );

  async function validateEmail(value: string | undefined) {
    if (!hasFocus) {
      return;
    }
    const emailPart = (value || '').split('@');
    if (!value || value.length === 0) {
      dispatch(updateN2NDeferredPasswordFieldValidationEmail(undefined));
      setValidationMessage(undefined);
      return;
    }
    if (
      (emailPart[0] || '').length > 64 ||
      (emailPart[1] || '').length > 253 ||
      !emailMatch.test(value)
    ) {
      dispatch(updateN2NDeferredPasswordFieldValidationEmail('error'));
      setValidationMessage(
        <SanitizedFormattedHtmlMessage {...messages.errorEmailInvalid} />
      );
      return;
    }
    dispatch(updateN2NDeferredPasswordFieldValidationEmail('validating'));
    void checkEmailAsyncDebounced(value);
  }

  return (
    <Field
      name="email"
      defaultValue=""
      label={intl.formatMessage(messages.inputLabelWorkEmail)}
      validate={validateEmail}
      isRequired
      isDisabled={false}
    >
      {({ fieldProps }: FieldType) => {
        return (
          <FieldWrapper
            id="field-wrapper"
            validationState={validationState}
            isDisabled={fieldProps.isDisabled}
          >
            <TextField
              className="text-field-desktop"
              autoComplete="email"
              isCompact={true}
              {...fieldProps}
              placeholder={intl.formatMessage(
                messages.inputPlaceholderWorkEmail
              )}
              data-testid="simple-signup-input-email"
              onFocus={() => setHasFocus(true)}
              onBlur={() => setHasFocus(false)}
              elemAfterInput={
                <IconValidation validationState={validationState} />
              }
            />
            {validationMessage && (
              <ErrorMessage>{validationMessage}</ErrorMessage>
            )}
          </FieldWrapper>
        );
      }}
    </Field>
  );
}
